 
import { useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import DOMPurify from 'dompurify';
import draftToHtml from 'draftjs-to-html';
import { marked } from 'marked';
import { Accordion, Alert, Button } from 'react-bootstrap';

import FAQVideos from './utils/FAQVideos';
import {
  IncrementFaqReadCountDocument,
  TripFaQsCategories,
  UserBookingDetailsQuery,
} from '@website/types/graphqlOperations';


interface TripFaqProps {
  faq: UserBookingDetailsQuery['booking']['trip']['faqs']['items'][0];
  showVideos: boolean;
  showLinks: boolean;
}

/**
 * Individual Trip FAQ
 */
export function TripFAQ({ faq, showVideos, showLinks }: TripFaqProps) {
  const [open, setOpen] = useState<boolean>(false);

  const [incrementTripFAQReadCounter] = useMutation(
    IncrementFaqReadCountDocument,
    {
      onError: () => {}, // Handle errors silently
    },
  );

  const markup = useMemo<{ __html?: string; text?: string }>(() => {
    try {
      return {
        __html: draftToHtml(JSON.parse(faq.answer)),
      };
    } catch (_e) {
      try {
        const html = DOMPurify.sanitize(
          marked.parseInline(faq.answer, {
            async: false,
            gfm: true,
            breaks: true,
          }),
        ).replaceAll(
          /<a href="([^"]+)">([^<]+)<\/a>/g,
          '<a href="$1" target="_blank" rel="noopener noreferrer">$2</a>',
        );

        // If there is an error, it means the FAQ is now in plain text (new format) so we can just return as is
        return {
          __html: html,
        };
      } catch (e) {
        return {
          text: faq.answer,
        };
      }
    }
  }, [faq.answer]);

  function headerClickHandler() {
    // Increment the read counter when opening only
    if (!open) {
      incrementTripFAQReadCounter({ variables: { id: faq.id } });
    }

    // Open/close the answer body
    setOpen(!open);
  }

  return (
    <Accordion.Item eventKey={faq.id}>
      <Accordion.Header onClick={headerClickHandler}>
        {`${faq.question}?`}
      </Accordion.Header>
      <Accordion.Body>
        {markup.__html && <div dangerouslySetInnerHTML={markup as any} />}
        {markup.text && <p style={{ whiteSpace: 'pre-line' }}>{markup.text}</p>}

        {showVideos && faq.videos && <FAQVideos videos={faq.videos} />}

        {showLinks && faq.links && (
          <div className="my-2">
            {faq.links.map((link) => (
              <Button
                key={link.url}
                href={link.url}
                className="me-1 mb-1"
                size="sm"
                target="_blank"
              >
                {link.title}
              </Button>
            ))}
          </div>
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
}

interface TripFaqsProps {
  faqs: UserBookingDetailsQuery['booking']['trip']['faqs']['items'];
  showVideos?: boolean;
  showLinks?: boolean;
  title?: string;
  titleClassName?: string;
  subTitleClassName?: string;
  notice?: string;
}

export default function ListTripFaqs({
  faqs,
  notice,
  showVideos = false,
  showLinks = false,
  title = 'FAQs',
  titleClassName = 'mb-4',
  subTitleClassName = 'mt-4 mb-4 card-header h5',
}: TripFaqsProps) {
  const beforeYourTripFAQs = faqs.filter(
    (faq) => faq.category === TripFaQsCategories.BEFORE_YOUR_TRIP,
  );

  const duringYourTripFAQs = faqs.filter(
    (faq) => faq.category === TripFaQsCategories.DURING_YOUR_TRIP,
  );

  const otherFAQs = faqs.filter(
    (faq) =>
      faq.category !== TripFaQsCategories.BEFORE_YOUR_TRIP &&
      faq.category !== TripFaQsCategories.DURING_YOUR_TRIP,
  );

  return (
    <>
      <h2 className={titleClassName}>{title}</h2>

      {notice && (
        <Alert variant="info">
          <span className="fw-bold">Important:</span> {notice}
        </Alert>
      )}

      {beforeYourTripFAQs?.length === 0 && duringYourTripFAQs.length === 0 ? (
        <>
          <Accordion>
            <>
              {faqs.map((faq) => (
                <TripFAQ
                  key={faq.id}
                  faq={faq}
                  showVideos={showVideos}
                  showLinks={showLinks}
                />
              ))}
            </>
          </Accordion>
        </>
      ) : (
        <>
          {beforeYourTripFAQs && (
            <>
              <h5 className={subTitleClassName}>Before you go</h5>
              <Accordion>
                <>
                  {beforeYourTripFAQs.map((faq) => (
                    <TripFAQ
                      key={faq.id}
                      faq={faq}
                      showVideos={showVideos}
                      showLinks={showLinks}
                    />
                  ))}
                </>
              </Accordion>
            </>
          )}

          {duringYourTripFAQs && (
            <>
              <h5 className={subTitleClassName}>On your trip</h5>
              <Accordion>
                <>
                  {duringYourTripFAQs.map((faq) => (
                    <TripFAQ
                      key={faq.id}
                      faq={faq}
                      showVideos={showVideos}
                      showLinks={showLinks}
                    />
                  ))}
                </>
              </Accordion>
            </>
          )}

          {otherFAQs && (
            <>
              <h5 className={subTitleClassName}>Other FAQs</h5>
              <Accordion>
                <>
                  {otherFAQs.map((faq) => (
                    <TripFAQ
                      key={faq.id}
                      faq={faq}
                      showVideos={showVideos}
                      showLinks={showLinks}
                    />
                  ))}
                </>
              </Accordion>
            </>
          )}
        </>
      )}
    </>
  );
}
